/**
 * Client-side image compression (§13.7: "camera-first, compress client-side
 * for 3G").
 *
 * ┌─ WHY THIS EXISTS ─────────────────────────────────────────────────────┐
 * │ A photo straight off a phone camera is 3-5 MB. On the 3G that reaches  │
 * │ most of the sites these vendors work at, that is a two-minute upload   │
 * │ that fails halfway and loses the proof — which means no payout, which  │
 * │ means a phone call to support. A 1600px JPEG at quality 0.72 is around │
 * │ 200 KB, carries every detail anyone will ever check in a dispute, and  │
 * │ uploads in a few seconds.                                              │
 * └────────────────────────────────────────────────────────────────────────┘
 *
 * Canvas, not a library. This is thirty lines and adding a dependency to the
 * bundle that has to load over the same 3G would be self-defeating.
 */

const MAX_EDGE = 1600;
const QUALITY = 0.72;

export interface CompressedImage {
  dataUrl: string;
  bytes: number;
  width: number;
  height: number;
}

export async function compressImage(file: File): Promise<CompressedImage> {
  // Anything that is not an image — a PDF challan, an audio file for a radio
  // job — passes through untouched. Re-encoding a PDF through a canvas would
  // produce a picture of a document.
  if (!file.type.startsWith('image/')) {
    const dataUrl = await readAsDataUrl(file);
    return { dataUrl, bytes: file.size, width: 0, height: 0 };
  }

  const bitmap = await loadBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    // No canvas: send the original rather than blocking the upload. A slow
    // proof is recoverable; a missing one is not.
    const dataUrl = await readAsDataUrl(file);
    return { dataUrl, bytes: file.size, width: 0, height: 0 };
  }

  ctx.drawImage(bitmap, 0, 0, width, height);
  if ('close' in bitmap) bitmap.close();

  const dataUrl = canvas.toDataURL('image/jpeg', QUALITY);

  return {
    dataUrl,
    // base64 inflates by 4/3; this is the real payload size, which is what the
    // "shrinking…" line reports.
    bytes: Math.round(((dataUrl.length - dataUrl.indexOf(',') - 1) * 3) / 4),
    width,
    height,
  };
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file);
    } catch {
      // Some Android browsers refuse HEIC here. Fall through to the img path.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('could not read that file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Current position, with a short timeout.
 *
 * Installation proof must carry coordinates (§13.5) and the API rejects it
 * without them. Waiting forever on a GPS fix inside a building is how the
 * upload screen appears to hang, so this gives up after 15 seconds and lets
 * the UI say so.
 */
export function getPosition(): Promise<{ latitude: number; longitude: number } | null> {
  if (!('geolocation' in navigator)) return Promise.resolve(null);

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 },
    );
  });
}
